/* eslint-disable */
import * as flatbuffers from "flatbuffers";
import { tracePointEmit } from "../../integrationTrace";
import { TracePointId } from "../../integrationTrace";
import { TraceOutcome } from "../../integrationTrace";

// ROOT
import { FrontEndMessage } from "../generated/front-end/front-end-message";
import { FrontEndMessageT } from "../generated/front-end/front-end-message";
import { BatchedFrontEndMessage } from "../generated/batched-front-end/batched-front-end-message";
import { BatchedFrontEndMessageT } from "../generated/batched-front-end/batched-front-end-message";

// LN2 message chain (T types)
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageContent } from "../generated/ln2/message-content";
import { MessageT } from "../generated/ln2/message";
import { BitMaskMessageT } from "../generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../generated/ln2/one-hot-message";

// TRAFFIC
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { ElevatorTrafficMessageT } from "../generated/traffic/elevator-traffic-message";
import { EscalatorTrafficMessageT } from "../generated/traffic/escalator-traffic-message";
import { TrafficMessageT } from "../generated/traffic/traffic-message";

// CONTROL
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ElevatorControlMessageT } from "../generated/control/elevator-control-message";
import { EscalatorControlMessageT } from "../generated/control/escalator-control-message";
import { ControlMessageT } from "../generated/control/control-message";

// USAGE
import { DeviceUsageContent } from "../generated/usage/device-usage-content";
import { ElevatorUsageMessageT } from "../generated/usage/elevator-usage-message";
import { EscalatorUsageMessageT } from "../generated/usage/escalator-usage-message";
import { UsageMessageT } from "../generated/usage/usage-message";

// FAULT
import { FaultContent } from "../generated/fault/fault-content";
import { FaultMessageT } from "../generated/fault/fault-message";

// SERVICE
import { DeviceServiceContent } from "../generated/service/device-service-content";
import { ServiceMessageT } from "../generated/service/service-message";

// STATUS
import { DeviceStatusContent } from "../generated/status/device-status-content";
import { StatusMessageT } from "../generated/status/status-message";
import { ElevatorStatusMessageT } from "../generated/status/elevator-status-message";
import { EscalatorStatusMessageT } from "../generated/status/escalator-status-message";
import { NormalStatusT } from "../generated/status/normal-status";
import { StatusVariant } from "../generated/status/status-variant";
import { Status } from "../generated/status/status";

// DEBOUNCE KEY
import {
  debounceKey,
  DEVICE_ELEVATOR,
  DEVICE_ESCALATOR,
  DEVICE_STATUS,
} from "../../utils/debounceLookup";

// ---------------------------------------------------------
// Helper: extract the inner enum code from a variant union
// ---------------------------------------------------------
function variantMessage(variant: any): number {
  if (variant && typeof variant.message === "number") return variant.message;
  return 0;
}

// ---------------------------------------------------------
// Map a server-side message_type_id to the corresponding
// MessageContent enum value. Returns the numeric id directly
// (same value — the API ids match the FlatBuffer enum).
// ---------------------------------------------------------
export function messageContentFromTypeId(id: number): number {
  return id;
}

// ---------------------------------------------------------
// Decode statistics — useful for diagnostics
// ---------------------------------------------------------
let decodeTotal = 0;
let decodeFailures = 0;

export function getDecodeStats(): { failures: number; total: number } {
  return { failures: decodeFailures, total: decodeTotal };
}

export function resetDecodeStats(): void {
  decodeTotal = 0;
  decodeFailures = 0;
}

function bytesToHex(bytes: Uint8Array, maxBytes: number): string {
  return Array.from(bytes.slice(0, maxBytes))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join(" ");
}

// ---------------------------------------------------------
// Robust root verifier (defense-in-depth).
//
// The generated FlatBuffer `verifyRootAs*` helpers structurally validate a
// buffer (bounds, vtable consistency, required fields, nesting limits) BEFORE
// any field is read — guarding against truncated, corrupt, or malicious frames.
//
// This wrapper keeps that verification robust to the helper being ABSENT: some
// flatc generations omit `verifyRootAs*`, and calling an undefined function
// directly would throw and null EVERY message (a regression we hit once when a
// stale schema checkout shipped without verifiers). Instead:
//   - verifier present + passes -> "ok"          (buffer validated)
//   - verifier present + throws -> "invalid"     (reject the buffer)
//   - verifier absent           -> "unavailable" (fall through to the unpack
//                                                 gate; never fail the decode)
// The "unavailable" case emits a one-time dev warning so a toolchain change
// that drops verifiers is noticed rather than silently disabling verification;
// the companion unit test asserts the verifier IS present in the shipped
// generation, catching that condition at CI time.
// ---------------------------------------------------------
const verifierUnavailableWarned = new Set<string>();

export function runRootVerifier(
  verifyFn: unknown,
  bytes: Uint8Array,
  label: string,
): "ok" | "invalid" | "unavailable" {
  if (typeof verifyFn !== "function") {
    if (process.env.NODE_ENV === "development" && !verifierUnavailableWarned.has(label)) {
      verifierUnavailableWarned.add(label);
      // eslint-disable-next-line no-console
      console.warn(
        `[FlatBuffer] verifyRootAs${label} is unavailable in the generated module — ` +
          "structural verification is disabled, relying on the unpack gate. " +
          "Check the flatbuffer_schemas generation.",
      );
    }
    return "unavailable";
  }

  try {
    (verifyFn as (view: DataView) => void)(
      new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength),
    );
    return "ok";
  } catch (e) {
    tracePointEmit(TracePointId.FrontendDecodeError, undefined, {
      outcome: TraceOutcome.Error,
      errorMessage: e instanceof Error ? e.message : String(e),
    });
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(
        `[FlatBuffer] Verification failed for ${label}: ${
          e instanceof Error ? e.message : String(e)
        }. Bytes[0:32]: ${bytesToHex(bytes, 32)}`,
      );
    }
    return "invalid";
  }
}

// ---------------------------------------------------------
// Binary dedup: cache last-seen raw bytes per bank+lift key
// ---------------------------------------------------------
const lastSeenBuffers: Map<string, Uint8Array> = new Map();

let dedupeStats = { total: 0, deduped: 0 };

export function getDedupeStats(): { total: number; deduped: number; ratio: number } {
  const { total, deduped } = dedupeStats;
  return { total, deduped, ratio: total === 0 ? 0 : deduped / total };
}

export function resetDedupeStats(): void {
  dedupeStats = { total: 0, deduped: 0 };
  lastSeenBuffers.clear();
}

/**
 * Build a string dedup key from the bankId, liftId, message content type,
 * and active flag — all read without calling unpack().
 * Including the content type discriminant and active flag ensures that
 * different message types on the same lift (e.g. Position vs Service) land
 * in separate dedup buckets, preventing OneHot messages whose inline bytes
 * share the same vtable layout from being incorrectly collapsed.
 * Returns null if either UUID is absent (message must not be deduped).
 */
function buildDedupeKey(fem: FrontEndMessage): string | null {
  const bankId = fem.bankId();
  const liftId = fem.liftId();
  if (!bankId || !liftId) return null;
  const bankVal = bankId.value();
  const liftVal = liftId.value();
  if (!bankVal || !liftVal) return null;
  const valuedMsg = fem.msg();
  const msgType = valuedMsg?.msg()?.contentType() ?? -1;
  const active = valuedMsg?.active() ?? false;
  return `${bankVal.upper()}_${bankVal.lower()}_${liftVal.upper()}_${liftVal.lower()}_${msgType}_${
    active ? 1 : 0
  }`;
}

/**
 * Extract the inline table bytes for a FrontEndMessage embedded in the batch
 * ByteBuffer. Uses the vtable to find the inline object size.
 * The inline data includes the vtable pointer and all inline field slots
 * (which are relative offsets to nested sub-tables). When the server packs
 * deterministically, identical content produces identical inline bytes.
 */
function extractFemInlineBytes(fem: FrontEndMessage, bb: flatbuffers.ByteBuffer): Uint8Array {
  const vtable = fem.bb_pos - bb.readInt32(fem.bb_pos);
  const objectSize = bb.readInt16(vtable + 2);
  return bb.bytes().slice(fem.bb_pos, fem.bb_pos + objectSize);
}

/**
 * Byte-for-byte comparison of two Uint8Arrays.
 */
function bytesEqual(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

// ---------------------------------------------------------
// MAIN: Decode FrontEndMessage bytes -> FrontEndMessageT
// ---------------------------------------------------------
export function decodeFrontEndMessage(bytes: Uint8Array): FrontEndMessageT | null {
  decodeTotal += 1;

  if (bytes.length < 4) {
    decodeFailures += 1;
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn(`[FlatBuffer] Message too short: ${bytes.length} bytes`);
    }
    return null;
  }

  try {
    // Defense-in-depth: structurally verify the buffer before unpacking. Robust
    // to the generated verifier being absent — see runRootVerifier(). A present
    // verifier that throws means the buffer is malformed and must be rejected.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { verifyRootAsFrontEndMessage } = require("../generated/front-end/front-end-message");
    if (runRootVerifier(verifyRootAsFrontEndMessage, bytes, "FrontEndMessage") === "invalid") {
      decodeFailures += 1;
      return null;
    }

    const bb = new flatbuffers.ByteBuffer(bytes);
    const fem = FrontEndMessage.getRootAsFrontEndMessage(bb).unpack();

    if (process.env.NODE_ENV === "development") {
      const encoded = fem.msg?.msg;
      const contentLabel = encoded
        ? (EncodedMessageContent[encoded.contentType] ?? encoded.contentType)
        : "none";
      const msgTypeLabel = encoded
        ? (() => {
            if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
              const bm = encoded.content as BitMaskMessageT;
              return MessageContent[bm?.messageType] ?? bm?.messageType;
            }
            if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
              const oh = encoded.content as OneHotMessageT;
              return MessageContent[oh?.messageType] ?? oh?.messageType;
            }
            return "?";
          })()
        : "none";
      // eslint-disable-next-line no-console
      console.log(
        `[DECODE] FEM: bankId=${!!fem.bankId} liftId=${!!fem.liftId} active=${
          fem.msg?.active
        } encoding=${contentLabel} msgType=${msgTypeLabel}`,
        fem.msg?.msg?.content,
      );
    }

    if (!fem.bankId || !fem.liftId) {
      decodeFailures += 1;
      if (process.env.NODE_ENV === "development") {
        const hex = bytesToHex(bytes, 32);
        const missing = [!fem.bankId && "bankId", !fem.liftId && "liftId"].filter(Boolean);
        // eslint-disable-next-line no-console
        console.warn(
          `[FlatBuffer] Missing identity fields (${missing.join(", ")}). Bytes[0:32]: ${hex}`,
        );
      }
      return null;
    }

    if (!fem.msg?.msg) {
      decodeFailures += 1;
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn(
          `[FlatBuffer] Message has identity but no payload (msg=${!!fem.msg}, msg.msg=${!!fem.msg
            ?.msg})`,
        );
      }
      return null;
    }

    tracePointEmit(TracePointId.FrontendFemDecoded);
    return fem;
  } catch (e) {
    decodeFailures += 1;
    tracePointEmit(TracePointId.FrontendDecodeError, undefined, {
      outcome: TraceOutcome.Error,
      errorMessage: e instanceof Error ? e.message : String(e),
    });
    if (process.env.NODE_ENV === "development") {
      const hex = bytesToHex(bytes, 32);
      // eslint-disable-next-line no-console
      console.warn(`[FlatBuffer] Decode error: ${e}. Bytes[0:32]: ${hex}`);
    }
    return null;
  }
}

// ---------------------------------------------------------
// Decode BatchedFrontEndMessage bytes -> BatchedFrontEndMessageT
// Sent at 5Hz from the cloud containing batched messages.
// Applies binary-level dedup: messages whose inline bytes match the
// last-seen bytes for their bank+lift key are skipped entirely (no unpack).
// ---------------------------------------------------------
export function decodeBatchedFrontEndMessage(bytes: Uint8Array): BatchedFrontEndMessageT | null {
  if (bytes.length < flatbuffers.SIZE_PREFIX_LENGTH + 4) return null;

  try {
    // Strip the 4-byte size prefix before verification — the server sends
    // length-prefixed FlatBuffers; verifyRootAs* reads position 0 as the root
    // offset, so the prefix must be removed first.
    const payload = bytes.subarray(flatbuffers.SIZE_PREFIX_LENGTH);

    // Defense-in-depth: structurally verify before unpacking. Robust to the
    // generated verifier being absent — see runRootVerifier().
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const {
      verifyRootAsBatchedFrontEndMessage,
    } = require("../generated/batched-front-end/batched-front-end-message");
    if (
      runRootVerifier(verifyRootAsBatchedFrontEndMessage, payload, "BatchedFrontEndMessage") ===
      "invalid"
    ) {
      return null;
    }

    const bb = new flatbuffers.ByteBuffer(bytes);
    const batch = BatchedFrontEndMessage.getSizePrefixedRootAsBatchedFrontEndMessage(bb);
    const count = batch.messagesLength();
    const filteredMessages: FrontEndMessageT[] = [];

    for (let i = 0; i < count; i += 1) {
      const fem = batch.messages(i);
      if (!fem) continue;

      dedupeStats.total += 1;

      const key = buildDedupeKey(fem);
      if (key !== null) {
        const inlineBytes = extractFemInlineBytes(fem, bb);
        const cached = lastSeenBuffers.get(key);
        if (cached !== undefined && bytesEqual(cached, inlineBytes)) {
          dedupeStats.deduped += 1;
          continue;
        }
        lastSeenBuffers.set(key, inlineBytes);
      }

      const unpacked = fem.unpack();
      filteredMessages.push(unpacked);

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { recordMessage } = require("../tools/liveMessageDiff");
        const bankVal = fem.bankId()?.value();
        const liftVal = fem.liftId()?.value();
        const bankDisplay = bankVal ? bankVal.upper().toString(16).slice(0, 8) : "unknown";
        const liftDisplay = liftVal ? liftVal.upper().toString(16).slice(0, 8) : "unknown";
        const builder = new flatbuffers.Builder(512);
        const offset = unpacked.pack(builder);
        FrontEndMessage.finishFrontEndMessageBuffer(builder, offset);
        const standaloneBytes = builder.asUint8Array();
        // Fire-and-forget — errors must not affect production message flow
        void recordMessage(
          key ?? `no-key-${i}`,
          `bank:${bankDisplay}`,
          `lift:${liftDisplay}`,
          standaloneBytes,
        );
      }
    }

    tracePointEmit(TracePointId.FrontendBatchDecoded);
    return new BatchedFrontEndMessageT(batch.timestamp(), filteredMessages);
  } catch (e) {
    tracePointEmit(TracePointId.FrontendDecodeError, undefined, {
      outcome: TraceOutcome.Error,
      errorMessage: e instanceof Error ? e.message : String(e),
    });
    if (process.env.NODE_ENV === "development") {
      const hex = bytesToHex(bytes, 32);
      // eslint-disable-next-line no-console
      console.warn(
        `[FlatBuffer] Exception in decodeBatchedFrontEndMessage: ${
          e instanceof Error ? e.message : String(e)
        }. Bytes[0:32]: ${hex}`,
      );
    }
    return null;
  }
}

// ---------------------------------------------------------
// Extract a "Connected" status from a StatusMessageT
// (replaces the old ConnectionStatusMessage extraction)
// ---------------------------------------------------------
export function extractConnectionStatus(
  statusMsg: StatusMessageT,
): ElevatorStatusMessageT | EscalatorStatusMessageT | null {
  switch (statusMsg.statusType) {
    case DeviceStatusContent.ElevatorStatusMessage: {
      const elev = statusMsg.status as ElevatorStatusMessageT | null;
      if (elev) {
        const code = variantMessage(elev.status);
        // Check if this is a connection-related status (Connected/Disconnected/Heartbeat)
        if (
          code === Status.Connected ||
          code === Status.Disconnected ||
          code === Status.Heartbeat
        ) {
          return elev;
        }
      }
      break;
    }
    case DeviceStatusContent.EscalatorStatusMessage: {
      const esc = statusMsg.status as EscalatorStatusMessageT | null;
      if (esc) {
        const code = variantMessage(esc.status);
        if (
          code === Status.Connected ||
          code === Status.Disconnected ||
          code === Status.Heartbeat
        ) {
          return esc;
        }
      }
      break;
    }
    default:
      break;
  }
  return null;
}

// ---------------------------------------------------------
// Check if a FrontEndMessageT is a fault message
// ---------------------------------------------------------
export function isFaultMessage(fem: FrontEndMessageT): boolean {
  const encoded = fem.msg?.msg;
  if (!encoded || encoded.contentType !== EncodedMessageContent.BitMaskMessage) return false;
  return (encoded.content as BitMaskMessageT).messageType === MessageContent.Fault_FaultMessage;
}

// ---------------------------------------------------------
// Compute a numeric composite debounce key from FlatBuffer
// union hierarchy.  Returns a packed integer encoding
// (deviceType, MessageContent, enumCode) — see debounceKey()
// — or undefined for unrecognised messages.
// ---------------------------------------------------------
export function computeDebounceKey(fem: FrontEndMessageT): number | undefined {
  return debounceKeyForEncoded(fem.msg?.msg);
}

/**
 * The same identity, computed from the encoded union alone.
 *
 * `computeDebounceKey` only ever read `fem.msg?.msg`, so this is that body with
 * the envelope peeled off — behaviour is unchanged and the public entry point
 * is now a one-line wrapper.
 *
 * It exists because a consumer holding a decoded socket message has the union
 * but not the `FrontEndMessageT` around it. Action verification is the case:
 * it must decide "is this the message the operator's action said to expect?",
 * and that question is exactly "do these two messages have the same identity" —
 * already answered here, for every category, through the full union hierarchy.
 * Re-deriving it beside this function would be a second definition of message
 * identity that could drift from the one debouncing uses.
 */
export function debounceKeyForEncoded(encoded: MessageT | null | undefined): number | undefined {
  if (!encoded) return undefined;

  if (encoded.contentType === EncodedMessageContent.BitMaskMessage) {
    const bm = encoded.content as BitMaskMessageT;
    switch (bm.messageType) {
      case MessageContent.Traffic_TrafficMessage: {
        const traffic = bm.message as TrafficMessageT;
        switch (traffic.messageType) {
          case DeviceTrafficMessage.ElevatorTrafficMessage:
            return debounceKey(
              DEVICE_ELEVATOR,
              MessageContent.Traffic_TrafficMessage,
              variantMessage((traffic.message as ElevatorTrafficMessageT).traffic),
            );
          case DeviceTrafficMessage.EscalatorTrafficMessage:
            return debounceKey(
              DEVICE_ESCALATOR,
              MessageContent.Traffic_TrafficMessage,
              variantMessage((traffic.message as EscalatorTrafficMessageT).traffic),
            );
          default:
            return undefined;
        }
      }
      case MessageContent.Control_ControlMessage: {
        const control = bm.message as ControlMessageT;
        switch (control.controlType) {
          case DeviceControlMessage.ElevatorControlMessage:
            return debounceKey(
              DEVICE_ELEVATOR,
              MessageContent.Control_ControlMessage,
              variantMessage((control.control as ElevatorControlMessageT).control),
            );
          case DeviceControlMessage.EscalatorControlMessage:
            return debounceKey(
              DEVICE_ESCALATOR,
              MessageContent.Control_ControlMessage,
              variantMessage((control.control as EscalatorControlMessageT).control),
            );
          default:
            return undefined;
        }
      }
      case MessageContent.Fault_FaultMessage: {
        const fault = bm.message as FaultMessageT;
        const devType =
          fault.faultType === FaultContent.ElevatorFaultMessage
            ? DEVICE_ELEVATOR
            : DEVICE_ESCALATOR;
        return debounceKey(
          devType,
          MessageContent.Fault_FaultMessage,
          variantMessage(fault.fault?.fault),
        );
      }
      case MessageContent.Status_StatusMessage: {
        const conn = extractConnectionStatus(bm.message as StatusMessageT);
        if (conn) {
          return debounceKey(
            DEVICE_STATUS,
            MessageContent.Status_StatusMessage,
            variantMessage(conn.status),
          );
        }
        return undefined;
      }
      default:
        return undefined;
    }
  }

  if (encoded.contentType === EncodedMessageContent.OneHotMessage) {
    const oh = encoded.content as OneHotMessageT;
    switch (oh.messageType) {
      case MessageContent.Usage_UsageMessage: {
        const usage = oh.message as UsageMessageT;
        switch (usage.messageType) {
          case DeviceUsageContent.ElevatorUsageMessage:
            return debounceKey(
              DEVICE_ELEVATOR,
              MessageContent.Usage_UsageMessage,
              variantMessage((usage.message as ElevatorUsageMessageT).usage),
            );
          case DeviceUsageContent.EscalatorUsageMessage:
            return debounceKey(
              DEVICE_ESCALATOR,
              MessageContent.Usage_UsageMessage,
              variantMessage((usage.message as EscalatorUsageMessageT).usage),
            );
          default:
            return undefined;
        }
      }
      case MessageContent.Service_ServiceMessage: {
        const service = oh.message as ServiceMessageT;
        const devType =
          service.messageType === DeviceServiceContent.ElevatorServiceMessage
            ? DEVICE_ELEVATOR
            : DEVICE_ESCALATOR;
        return debounceKey(
          devType,
          MessageContent.Service_ServiceMessage,
          variantMessage(service.message?.message),
        );
      }
      default:
        return undefined;
    }
  }

  return undefined;
}
